import {firestore} from 'firebase/app'
import {FirestoreQuery} from '../Firestore/FirestoreQuery'
import {Order} from './Order'
import {OrderMapper} from './OrderMapper'
import {OrderStatusEnum} from './OrderStatusEnum'
import {OrderTypeEnum} from './OrderTypeEnum'
import {OrderEntry} from './OrderEntry/OrderEntry'
import {v4 as uuid} from 'uuid'
import moment from 'moment'

export class OrderCollection {
  protected collection: string = 'order'

  constructor(protected db: firestore.Firestore, protected batch?: firestore.WriteBatch) {}

  watchOrdersByCompanyId(companyId: string, cb?: (orders: Order[]) => void): Order[] {
    const query = this.initQuery()
    query.setQuery(this.getOrdersByCompanyIdQuery(query, companyId))
    return query.watchList(cb)
  }

  watchApprovedOrdersByCompanyId(companyId: string, cb?: (orders: Order[]) => void): Order[] {
    const query = this.initQuery()
    query.setQuery(this.getApprovedOrdersByCompanyIdQuery(query, companyId))
    return query.watchList(cb)
  }

  watchVendorOrdersByVendorRepId(vendorRepId: string, cb?: (orders: Order[]) => void): Order[] {
    const query = this.initQuery()
    query.setQuery(this.getVendorOrdersByVendorRepId(query, vendorRepId))
    return query.watchList(cb)
  }

  watchVendorOrdersByVendorRepIdAndCompanyId(companyId: string, vendorRepId: string, cb?: (orders: Order[]) => void): Order[] {
    const query = this.initQuery()
    query.setQuery(this.getVendorOrdersByVendorRepIdAndCompanyId(query, companyId, vendorRepId))
    return query.watchList(cb)
  }

  watchVendorOrdersByRestaurantId(companyId: string, cb?: (orders: Order[]) => void): Order[] {
    const query = this.initQuery()
    query.setQuery(this.getVendorOrdersByRestaurantId(query, companyId))
    return query.watchList(cb)
  }

  watchOrdersByCompanyIds(companyIds: string[]): Order[] {
    return this.watchLists(
      companyIds,
      (id, cb) => this.watchOrdersByCompanyId(id, cb)
    )
  }

  watchVendorOrdersByRestaurantIds(companyIds: string[]): Order[] {
    return this.watchLists(
      companyIds,
      (id, cb) => this.watchVendorOrdersByRestaurantId(id, cb)
    )
  }

  watchOrdersByRestaurantAndVendor(restaurantId: string, vendorId: string): Order[] {
    const query = this.initQuery()
    query.setQuery(this.getOrdersByRestaurantIdAndVendorIdQuery(query, restaurantId, vendorId))
    return query.watchList()
  }

  watch(id: string): { data: Order | null } {
    const query = this.initQuery()
    return query.watch(id)
  }

  get(id: string): Promise<Order | null> {
    const query = this.initQuery()
    return query.find(id)
  }

  list(): Promise<Order[]> {
    const query = this.initQuery()
    return query.list()
  }

  listByDateRange(beginDate: string, endDate: string, orderType?: OrderTypeEnum): Promise<Order[]> {
    const query = this.initQuery()
    orderType
      ? query.setQuery(this.listOrdersByDatePlacedRangeQueryAndOrderType(query, beginDate, endDate, orderType))
      : query.setQuery(this.listOrdersByDatePlacedRangeQuery(query, beginDate, endDate))
    return query.list()
  }

  listByDateRangeAndRestaurantId(beginDate: string, endDate: string, restaurantId: string, orderType?: OrderTypeEnum): Promise<Order[]> {
    const query = this.initQuery()
    orderType
      ? query.setQuery(this.listOrdersByDatePlacedRangeQueryAndRestaurantIdAndOrderType(query, beginDate, endDate, restaurantId, orderType))
      : query.setQuery(this.listOrdersByDatePlacedRangeQueryAndRestaurantId(query, beginDate, restaurantId, endDate))
    return query.list()
  }

  listOrdersByCompanyId(companyId: string): Promise<Order[]> {
    const query = this.initQuery()
    query.setQuery(this.getOrdersByCompanyIdQuery(query, companyId))
    return query.list()
  }

  listVendorOrdersByRestaurantId(companyId: string): Promise<Order[]> {
    const query = this.initQuery()
    query.setQuery(this.getVendorOrdersByRestaurantId(query, companyId))
    return query.list()
  }

  listVendorOrdersByRestaurantOrderId(orderId: string): Promise<Order[]> {
    const query = this.initQuery()
    query.setQuery(this.getVendorOrdersByRestaurantOrderId(query, orderId))
    return query.list()
  }

  listByType(type: OrderTypeEnum): Promise<Order[]> {
    const query = this.initQuery()
    query.setQuery(this.getOrdersByType(query, type))
    return query.list()
  }

  listVendorOrdersByVendorRepId(repId: string): Promise<Order[]> {
    const query = this.initQuery()
    query.setQuery(this.getVendorOrdersByVendorRepId(query, repId))
    return query.list()
  }

  async getVendorOrderByRestaurantOrderIdAndVendorId(orderId: string, vendorCompanyId: string): Promise<Order | null> {
    const query = this.initQuery()
    query.setQuery(this.getOrdersByRestaurantOrderIdAndVendorId(query, orderId, vendorCompanyId))
    const orders = await query.list()

    return orders.length ? orders[0] : null
  }

  async vendorRepHasOrdersFromRestaurant(vendorRepId: string, restaurantId: string) {
    const query = this.initQuery()
    query.setQuery(
      this.getOrdersByRestaurantIdAndVendorRepId(query, restaurantId, vendorRepId).limit(1)
    )
    return !!(await query.list()).length
  }

  async getBackorderByRestaurantOrderId(orderId: string, companyId: string) {
    const query = this.initQuery()
    query.setQuery(
      query.collection(this.collection)
        .where('originalOrderId', '==', orderId)
        .where('orderedByCompanyId', '==', companyId)
    )
    const backorders = await query.list()
    return backorders.length ? backorders[0] : null
  }

  listApprovedOrdersByRestaurantIdAndDatePlacedRange(restaurantId: string, startOfDay: string, endOfDay:string){
    const query = this.initQuery()
    query.setQuery(this.listApprovedOrdersByRestaurantIdAndDatePlacedRangeQuery(query, restaurantId, startOfDay, endOfDay))
    return query.list();
  }

  listVendorOrdersByRestaurantIdandCheckedInRange(restaurantId: string, startOfDay: string, endOfDay: string){
    const query = this.initQuery()
    query.setQuery(this.listVendorOrdersByRestaurantIdandCheckedInRangeQuery(query, restaurantId, startOfDay, endOfDay))
    return query.list();
  }

  listOrdersByOriginalOrderIdAndRestaurantId(originalOrderId: string, restaurantId: string){
    const query = this.initQuery()
    query.setQuery(this.listOrdersByOriginalOrderIdAndRestaurantIdQuery(query, originalOrderId , restaurantId))
    return query.list();
  }

  listdOrdersByTypeAndDatePlacedRange(orderType: OrderTypeEnum, startDate: string, endDate:string){
    const query = this.initQuery()
    query.setQuery(this.listdOrdersByTypeAndDatePlacedRangeQuery(query, orderType, startDate, endDate))
    return query.list();
  }

  save(order: Order): Promise<Order> {
    // dedup entries
    const dedupedEntries = new Map<string, OrderEntry>();
    order.orderEntries.forEach(entry => {
      entry.description = entry.description.trim()
      if(entry.orderingNotes) {
        entry.orderingNotes = entry.orderingNotes.trim()
      }

      if(entry.receivingNotes) {
        entry.receivingNotes = entry.receivingNotes.trim()
      }

      if(entry.quantityType) {
        entry.quantityType = entry.quantityType.trim()
      }

      if(entry.category) {
        entry.category = entry.category.trim()
      }

      const key =
        entry.description
        + entry.quantityType
        + entry.category
        + entry.upc
        + entry.priceListId
        + entry.vendorCompanyId;
      const existingEntry = dedupedEntries.get(key);
      if (!existingEntry) {
        dedupedEntries.set(key, entry);
      } else {
        existingEntry.quantity += entry.quantity;
        existingEntry.quantityType = entry.quantityType;
        if (existingEntry.orderingNotes && entry.orderingNotes) {
          existingEntry.orderingNotes += `\n\n${entry.orderingNotes}`
        } else {
          existingEntry.orderingNotes = existingEntry.orderingNotes || entry.orderingNotes
        }
      }

      if(!entry.upc || entry.upc === '') {
        entry.upc = uuid()
      }
    })
    order.orderEntries = [...dedupedEntries.values()];
    order.number = order.number.trim()

    const query = this.initQuery()
    return query.save(order)
  }

  saveDraft(order: Order): Promise<Order> {
    if (!order.number || !order.number.length || order.number.startsWith('DRAFT - ')) {
      order.number = moment().format('lll')
    }

    const query = this.initQuery()
    return query.save(order)
  }

  async delete(order: Order) {
    if (order.canDelete) {
      const query = this.initQuery();
      return query.delete(order)
    }
  }

  protected initQuery(): FirestoreQuery<Order> {
    const query = new FirestoreQuery<Order>(this.db, new OrderMapper())
    query.collection(this.collection)

    return query
  }

  protected getOrdersByCompanyIdQuery(query: FirestoreQuery<Order>, companyId: string) {
    return query.collection(this.collection).where('assignedCompanyId', '==', companyId)
  }

  protected getOrdersByType(query: FirestoreQuery<Order>, type: OrderTypeEnum) {
    return query.collection(this.collection).where('type', '==', type)
  }

  protected getApprovedOrdersByCompanyIdQuery(query: FirestoreQuery<Order>, companyId: string) {
    return this.getOrdersByCompanyIdQuery(query, companyId).where('status', '==', OrderStatusEnum.APPROVED)
  }

  protected getVendorOrdersByVendorRepIdAndCompanyId(query: FirestoreQuery<Order>, companyId: string, vendorRepId: string) {
    return this.getOrdersByCompanyIdQuery(query, companyId)
        .where('vendorRepId', '==', vendorRepId)
        .where('type', '==', OrderTypeEnum.VENDOR)
  }

  protected getVendorOrdersByRestaurantId(query: FirestoreQuery<Order>, companyId: string) {
    return query.collection(this.collection)
        .where('orderedByCompanyId', '==', companyId)
        .where('type', '==', OrderTypeEnum.VENDOR)
  }

  protected getVendorOrdersByVendorRepId(query: FirestoreQuery<Order>, vendorRepId: string) {
    return query.collection(this.collection)
        .where('vendorRepId', '==', vendorRepId)
        .where('type', '==', OrderTypeEnum.VENDOR)
  }

  protected getOrdersByRestaurantOrderIdAndVendorId(query: FirestoreQuery<Order>, orderId: string, vendorId: string) {
    return query.collection(this.collection)
        .where('restaurantOrderId', '==', orderId)
        .where('assignedCompanyId', '==', vendorId)
  }

  protected getVendorOrdersByRestaurantOrderId(query: FirestoreQuery<Order>, orderId: string) {
    return query.collection(this.collection)
        .where('restaurantOrderId', '==', orderId)
        .where('type', '==', OrderTypeEnum.VENDOR)
  }

  protected getOrdersByRestaurantIdAndVendorRepId(query: FirestoreQuery<Order>, restaurantId: string, vendorRepId: string) {
    return query.collection(this.collection)
        .where('orderedByCompanyId', '==', restaurantId)
        .where('vendorRepId', '==', vendorRepId)
  }

  protected getOrdersByRestaurantIdAndVendorIdQuery(query: FirestoreQuery<Order>, restaurantId: string, vendorId: string) {
    return query.collection(this.collection)
        .where('orderedByCompanyId', '==', restaurantId)
        .where('assignedCompanyId', '==', vendorId)
  }

  protected listApprovedOrdersByRestaurantIdAndDatePlacedRangeQuery(query: FirestoreQuery<Order>, restaurantId: string, startOfDay: string, endOfDay: string) {
    return query.collection(this.collection)
        .where('type', '==', OrderTypeEnum.RESTAURANT)
        .where('orderedByCompanyId', '==', restaurantId)
        .where('datePlaced', '>=', startOfDay)
        .where('datePlaced', '<=', endOfDay)
        .where('status','==',OrderStatusEnum.APPROVED)
  }

  protected listVendorOrdersByRestaurantIdandCheckedInRangeQuery(query: FirestoreQuery<Order>, restaurantId: string, startOfDay: string, endOfDay: string) {
    return query.collection(this.collection)
        .where('type', '==', OrderTypeEnum.VENDOR)
        .where('orderedByCompanyId', '==', restaurantId)
        .where('dateCheckedIn', '>=', startOfDay)
        .where('dateCheckedIn', '<=', endOfDay)
  }

  protected listOrdersByDatePlacedRangeQuery(query: FirestoreQuery<Order>, beginDate: string, endDate: string) {
    return query.collection(this.collection)
        .where('datePlaced', '>=', beginDate)
        .where('datePlaced', '<=', endDate)
  }

  protected listOrdersByDatePlacedRangeQueryAndOrderType(query: FirestoreQuery<Order>, beginDate: string, endDate: string, orderType: OrderTypeEnum) {
    return query.collection(this.collection)
      .where('datePlaced', '>=', beginDate)
      .where('datePlaced', '<=', endDate)
      .where('type', '==', orderType)
  }

  protected listOrdersByDatePlacedRangeQueryAndRestaurantId(query: FirestoreQuery<Order>, beginDate: string, endDate: string, restaurantId: string) {
    return query.collection(this.collection)
      .where('datePlaced', '>=', beginDate)
      .where('datePlaced', '<=', endDate)
      .where('orderedByCompanyId', '==', restaurantId)
  }

  protected listOrdersByDatePlacedRangeQueryAndRestaurantIdAndOrderType(
    query: FirestoreQuery<Order>,
    beginDate: string,
    endDate: string,
    restaurantId: string,
    orderType: OrderTypeEnum
  ) {
    return query.collection(this.collection)
      .where('datePlaced', '>=', beginDate)
      .where('datePlaced', '<=', endDate)
      .where('type', '==', orderType)
      .where('orderedByCompanyId', '==', restaurantId)
  }

  protected listOrdersByOriginalOrderIdAndRestaurantIdQuery(query: FirestoreQuery<Order>,originalOrderId: string  ,restaurantId: string) {
    return query.collection(this.collection)
        .where('type', '==', OrderTypeEnum.RESTAURANT)
        .where('originalOrderId','==',originalOrderId)
        .where('orderedByCompanyId', '==', restaurantId)
  }

  protected listdOrdersByTypeAndDatePlacedRangeQuery(query: FirestoreQuery<Order>, orderType: OrderTypeEnum, startDate: string, endDate: string) {
    return query.collection(this.collection)
      .where('type', '==', orderType)
      .where('datePlaced', '>=', startDate)
      .where('datePlaced', '<=', endDate)
  }

  protected watchLists<T>(items: T[], action: (item: T, resultCb: (orders: Order[]) => void) => void) {
    const orders: Order[] = []
    const ordersMap: Map<T, Order[]> = items.reduce(
      (map, item) => map.set(item, []),
      new Map()
    )

    items.forEach(item => action(item, itemOrders => {
      ordersMap.set(item, itemOrders)
      orders.splice(
        0,
        orders.length,
        ...[...ordersMap.values()].reduce((a, c) => ([...a, ...c]), [])
      )
    }))

    return orders
  }
}
